import { times, flatten, isEqual } from "lodash";
import { Observable } from "rxjs";

import { OrdersRepository } from "lib/repositories/index";
import { OrderFullDecorator } from "lib/decorators/index";
import { OrderedProductOrderFieldDecorator } from "lib/decorators/index";
import { OrderItemDecorator } from "lib/decorators/index";
import st from "lib/utils/index";

const addOrderItemToOrder = (order, orderItem) => {
  const nextOrder = new OrderFullDecorator({
    ...order,
    order_items: order.order_items.concat([orderItem]),
  });
  nextOrder.recalculate();
  return nextOrder;
};

const updateOrderItemToOrder = (order, orderItem) => {
  const nextOrder = new OrderFullDecorator({
    ...order,
    order_items: order.order_items.map((oi) => (oi.uuid === orderItem.uuid ? orderItem : oi)),
  });
  nextOrder.recalculate();
  return nextOrder;
};

const upsertOrderItemToOrder = (order, orderItem) =>
  order.order_items.find((oi) => oi.uuid === orderItem.uuid)
    ? updateOrderItemToOrder(order, orderItem)
    : addOrderItemToOrder(order, orderItem);

const removeOrderItemToOrder = (order, orderItem) => {
  const nextOrder = new OrderFullDecorator({
    ...order,
    order_items: order.order_items.filter((oi) => oi.uuid !== orderItem.uuid),
  });
  nextOrder.recalculate();
  return nextOrder;
};

const addOrderedItemChangeOrderItemToOrder = (initialOrder, order, newOrderedItem) => {
  const initialOrderedItem = initialOrder.ordered_product_pricelist_items.find(
    (orderedItem) => orderedItem.uuid === newOrderedItem.uuid
  );

  // Compute the difference
  const quantityDifference = newOrderedItem.quantity - initialOrderedItem.quantity;

  if (quantityDifference !== 0) {
    const newOrderItem = new OrderItemDecorator({
      uuid: newOrderedItem.uuid,
      item_type: "product_item",
      base_item: false,
      name: I18n.t("papp.orders.EditOrderItemsModal.changed_order_item_name", {
        item_name: newOrderedItem.product_pricelist_item.display_name,
      }),
      quantity: Math.abs(quantityDifference),
      unit_price: {
        value: quantityDifference >= 0 ? newOrderedItem.unit_price.value : -newOrderedItem.unit_price.value,
        currency: newOrderedItem.unit_price.currency,
      },
      position: 1000,
    });
    return upsertOrderItemToOrder(order, newOrderItem);
  } else {
    return removeOrderItemToOrder(order, { uuid: newOrderedItem.uuid });
  }
};

const changeOrderedItemInOrder = (initialOrder, order, newOrderedItem) => {
  order = new OrderFullDecorator({
    ...order,
    ordered_product_pricelist_items: order.ordered_product_pricelist_items.map((orderedItem) =>
      orderedItem.uuid === newOrderedItem.uuid ? newOrderedItem : orderedItem
    ),
  });
  order = addOrderedItemChangeOrderItemToOrder(initialOrder, order, newOrderedItem);
  order = regenerateOrderParticipants(initialOrder, order);
  return order;
};

const regenerateOrderParticipants = (initialOrder, order) => {
  order.ordered_product_pricelist_items.forEach((orderedItem) => {
    const orderedItemParticipants = order.order_customer_participants.filter(
      (p) => p.ordered_product_pricelist_item && p.ordered_product_pricelist_item.uuid === orderedItem.uuid
    );
    if (orderedItemParticipants.length < orderedItem.persons_count) {
      const participantTemplate = order.order_customer_participants[0];
      const participantOrderedFields = participantTemplate ? participantTemplate.ordered_product_order_fields : [];
      const newParticipants = times(orderedItem.persons_count - orderedItemParticipants.length).map((i) => {
        return {
          uuid: st.generateUuid(),
          ordered_product_pricelist_item: orderedItem,
          ordered_product_order_fields: participantOrderedFields.map((orderedField) => ({
            ...orderedField,
            id: undefined,
            uuid: st.generateUuid(),
            value: null,
          })),
          position: Math.max(...order.order_customer_participants.map((p) => p.position)) + 1,
        };
      });
      order = new OrderFullDecorator({
        ...order,
        order_customer_participants: order.order_customer_participants.concat(newParticipants),
      });
    } else if (orderedItemParticipants.length > orderedItem.persons_count) {
      const participantsToRemove = orderedItemParticipants.slice(orderedItem.persons_count).filter((p) => !p.id);
      participantsToRemove.forEach((participant) => {
        order = removeOrderParticipantFromOrder(initialOrder, order, participant);
      });
    }
  });

  order.recalculateParticipantsIsExtraAttribute();
  return order;
};

const removeOrderParticipantFromOrder = (initialOrder, order, participant) => {
  const orderedFieldsToRemove = participant.ordered_product_order_fields
    .filter((of) => of.product_order_field.has_pricing)
    .filter((of) => of.value !== null);
  if (orderedFieldsToRemove.length > 0) {
    order = orderedFieldsToRemove.reduce((order, orderedField) => {
      return changeOrderedFieldInOrder(initialOrder, order, {
        ...orderedField,
        value: null,
        quantity: 0,
        unit_price: null,
        unit_quantity: 0,
        price: null,
      });
    }, order);
  }

  order = new OrderFullDecorator({
    ...order,
    order_customer_participants: order.order_customer_participants.filter((p) => p.uuid !== participant.uuid),
  });
  order.recalculateParticipantsIsExtraAttribute();
  return order;
};

const changeOrderedFieldInOrder = (initialOrder, order, newOrderedField) => {
  const initialOrderedField = initialOrder.all_ordered_product_order_fields.find(
    (orderedField) => orderedField.uuid === newOrderedField.uuid
  );

  const newPrice = newOrderedField.price;
  const oldPrice = initialOrderedField && initialOrderedField.price;
  const currency = (newPrice && newPrice.currency) || (oldPrice && oldPrice.currency) || order.product_price_currency;

  if (oldPrice && newPrice && newPrice.currency !== oldPrice.currency) {
    const orderWithoutOrderedField = changeOrderedFieldInOrder(initialOrder, order, {
      ...newOrderedField,
      value: null,
      quantity: 0,
      unit_price: null,
      unit_quantity: 0,
      price: null,
    });
    return changeOrderedFieldInOrder(initialOrder, orderWithoutOrderedField, newOrderedField);
  }

  let name;
  if (initialOrderedField && initialOrderedField.order_item_name && newOrderedField.order_item_name) {
    name = I18n.t("papp.orders.EditOrderItemsModal.changed_order_option_name", {
      prev_item_name: initialOrderedField.order_item_name,
      next_item_name: newOrderedField.order_item_name,
    });
  } else if (initialOrderedField && initialOrderedField.order_item_name && !newOrderedField.order_item_name) {
    name = I18n.t("papp.orders.EditOrderItemsModal.removed_order_option_name", {
      prev_item_name: initialOrderedField.order_item_name,
    });
  } else {
    name = I18n.t("papp.orders.EditOrderItemsModal.added_order_option_name", {
      next_item_name: newOrderedField.order_item_name,
    });
  }

  const totalPriceDifference = ((newPrice && newPrice.value) || 0) - ((oldPrice && oldPrice.value) || 0);
  const newItem =
    initialOrderedField && isEqual(initialOrderedField.value, newOrderedField.value)
      ? null
      : new OrderItemDecorator({
          uuid: st.generateUuid(),
          item_type: "product_option",
          base_item: false,
          name,
          quantity: 1,
          unit_price: {
            value: totalPriceDifference,
            currency,
          },
          _orderedFieldUuid: newOrderedField.uuid,
          position: 2000,
        });

  return new OrderFullDecorator({
    ...order,
    order_items: order.order_items
      .filter((item) => item._orderedFieldUuid !== newOrderedField.uuid)
      .concat(newItem ? [newItem] : []),
    ordered_product_order_fields: order.ordered_product_order_fields.map((orderedField) =>
      orderedField.uuid === newOrderedField.uuid ? newOrderedField : orderedField
    ),
    order_customer_participants: order.order_customer_participants.map((participant) => ({
      ...participant,
      ordered_product_order_fields: participant.ordered_product_order_fields.map((orderedField) =>
        orderedField.uuid === newOrderedField.uuid ? newOrderedField : orderedField
      ),
    })),
  });
};

const validateOrder = (order, { submitValidate }) => {
  const errors = new st.form.ValidationErrors();

  const productQuantity = order.ordered_product_pricelist_items
    .map((orderedItem) => orderedItem.quantity)
    .reduce((a, b) => a + b, 0);
  errors.add(
    `base`,
    st.validators.presence(productQuantity) && I18n.t("activerecord.errors.models.order.too_low_product_quantity")
  );

  order.ordered_product_order_fields
    .filter((of) => of.product_order_field.has_pricing)
    .forEach((orderedField) => {
      if (orderedField.product_order_field.order_value_required) {
        // errors.add(`order.ordered_product_order_fields[${orderedField.uuid}].value`, st.validators.presence(orderedField.value))
      }
    });

  order.order_customer_participants.forEach((participant) => {
    participant.ordered_product_order_fields
      .filter((of) => of.product_order_field.has_pricing)
      .forEach((orderedField) => {
        if (orderedField.product_order_field.order_value_required) {
          // errors.add(`order.order_customer_participants[${participant.uuid}].ordered_product_order_fields[${orderedField.uuid}].value`, st.validators.presence(orderedField.value))
        }
      });
  });

  order.order_items
    .filter((item) => item.is_new_custom_item)
    .forEach((item) => {
      submitValidate && errors.add(`order.order_items[${item.uuid}].name`, st.validators.presence(item.name));
      submitValidate &&
        errors.add(
          `order.order_items[${item.uuid}].unit_price`,
          st.validators.presence(item.unit_price && item.unit_price.value && item.unit_price.currency)
        );
    });

  errors.add("order.agent_commision_amount", st.validators.presence(order.agent_commision_amount));

  if (errors.length > 0) {
    errors.add("base", I18n.t("papp.common.form_validation_error_message"));
  }

  return errors;
};

export const OPEN = "orders/EditOrderItemsModal/OPEN";
export const CLOSE = "orders/EditOrderItemsModal/CLOSE";
export const ADD_CUSTOM_ITEM = "orders/EditOrderItemsModal/ADD_CUSTOM_ITEM";
export const UPDATE_CUSTOM_ITEM = "orders/EditOrderItemsModal/UPDATE_CUSTOM_ITEM";
export const REMOVE_CUSTOM_ITEM = "orders/EditOrderItemsModal/REMOVE_CUSTOM_ITEM";
export const UPDATE_ORDERED_ITEM = "orders/EditOrderItemsModal/UPDATE_ORDERED_ITEM";
export const UPDATE_ORDERED_FIELD = "orders/EditOrderItemsModal/UPDATE_ORDERED_FIELD";
export const REMOVE_ORDER_PARTICIPANT = "orders/EditOrderItemsModal/REMOVE_ORDER_PARTICIPANT";
export const UPDATE_ORDER = "orders/EditOrderItemsModal/UPDATE_ORDER";
export const VALIDATE = "orders/EditOrderItemsModal/VALIDATE";
export const SUBMIT = "orders/EditOrderItemsModal/SUBMIT";
export const FETCH_SUBMIT_START = "orders/EditOrderItemsModal/FETCH_SUBMIT_START";
export const FETCH_SUBMIT_SUCCESS = "orders/EditOrderItemsModal/FETCH_SUBMIT_SUCCESS";
export const FETCH_SUBMIT_FAILURE = "orders/EditOrderItemsModal/FETCH_SUBMIT_FAILURE";

export const stateSelector = (state) => state.orders.EditOrderItemsModal;

const initialState = {
  opened: false,
  initialOrder: null,
  order: null,
  errors: new st.form.ValidationErrors(),
  valid: true,
  submitLoading: false,
  saved: false,
};

const reducers = {
  [OPEN]: (state, { order }) => {
    return {
      ...initialState,
      opened: true,
      initialOrder: order,
      order,
    };
  },

  [CLOSE]: (state) => {
    return {
      ...state,
      opened: false,
    };
  },

  [VALIDATE]: (state, { submitValidate }) => {
    const errors = validateOrder(state.order, { submitValidate });
    return {
      ...state,
      errors,
      valid: errors.valid,
    };
  },

  [FETCH_SUBMIT_START]: (state) => {
    return {
      ...state,
      submitLoading: true,
      errors: new st.form.ValidationErrors(),
    };
  },

  [FETCH_SUBMIT_SUCCESS]: (state, { order }) => {
    return {
      ...state,
      initialOrder: order,
      saved: true,
      submitLoading: false,
    };
  },

  [FETCH_SUBMIT_FAILURE]: (state, { errors }) => {
    return {
      ...state,
      submitLoading: false,
      errors,
    };
  },

  [UPDATE_ORDER]: (state, { order }) => ({
    ...state,
    order: new OrderFullDecorator(order),
  }),

  [ADD_CUSTOM_ITEM]: (state, { orderItem }) => {
    return {
      ...state,
      order: addOrderItemToOrder(state.order, orderItem),
    };
  },

  [UPDATE_CUSTOM_ITEM]: (state, { orderItem }) => {
    return {
      ...state,
      order: updateOrderItemToOrder(state.order, orderItem),
    };
  },

  [REMOVE_CUSTOM_ITEM]: (state, { orderItem }) => {
    return {
      ...state,
      order: removeOrderItemToOrder(state.order, orderItem),
    };
  },

  [UPDATE_ORDERED_ITEM]: (state, { newOrderedItem }) => {
    return {
      ...state,
      order: changeOrderedItemInOrder(state.initialOrder, state.order, newOrderedItem),
    };
  },

  [UPDATE_ORDERED_FIELD]: (state, { newOrderedField }) => {
    return {
      ...state,
      order: changeOrderedFieldInOrder(state.initialOrder, state.order, newOrderedField),
    };
  },

  [REMOVE_ORDER_PARTICIPANT]: (state, { participant }) => {
    return {
      ...state,
      order: removeOrderParticipantFromOrder(state.initialOrder, state.order, participant),
    };
  },
};

const epics = [
  (actions$, { getState, dispatch }) =>
    actions$
      .ofType(SUBMIT)
      .switchMap(() => {
        setTimeout(() => {
          dispatch(validate({ submitValidate: true }));
        });
        return actions$.ofType(VALIDATE).take(1);
      })
      .filter(() => {
        const { valid } = stateSelector(getState());
        return valid;
      })
      .flatMap(() => {
        const { order, initialOrder } = stateSelector(getState());
        const mapOrderToData = (order) => ({
          id: order.id,
          token: order.token,
          agent_commision_amount: order.agent_commision_amount,
          order_items: order.order_items.filter((item) => !item.id),
          ordered_product_order_fields: order.priced_ordered_fields,
          ordered_product_pricelist_items: order.ordered_product_pricelist_items
            ? order.ordered_product_pricelist_items.map((oi) => ({
                ...oi,
                ordered_product_order_fields: oi.priced_ordered_fields,
              }))
            : undefined,
          order_customer_participants: order.order_customer_participants
            ? order.order_customer_participants.map((participant) => ({
                id: participant.id,
                position: participant.position,
                ordered_product_order_fields: participant.priced_ordered_fields,
              }))
            : undefined,
        });
        return Observable.concat(
          Observable.of({ type: FETCH_SUBMIT_START }),
          OrdersRepository.updateItems(
            mapOrderToData(order),
            mapOrderToData({
              ...initialOrder,
              order_items: [],
            })
          )
            .map((order) => {
              return { type: FETCH_SUBMIT_SUCCESS, order };
            })
            .catch((error) => {
              st.api.handleError(error, { getState, dispatch });
              const errors = new st.form.ValidationErrors([["base", st.api.findResponseErrorMessage(error)]]);
              return Observable.of({
                type: FETCH_SUBMIT_FAILURE,
                errors,
                error,
              });
            })
        );
      }),
];

export function open(order) {
  return { type: OPEN, order };
}

export function close() {
  return { type: CLOSE };
}

export function addCustomItem() {
  return (dispatch, getState) => {
    const { order } = stateSelector(getState());
    const orderItem = new OrderItemDecorator({
      uuid: st.generateUuid(),
      item_type: "product_custom",
      base_item: false,
      name: "",
      quantity: 1,
      unit_price: { value: 0, currency: order.product_price_currency },
      position: 3000,
    });
    dispatch({ type: ADD_CUSTOM_ITEM, orderItem });
  };
}

export function updateCustomItem(orderItem) {
  orderItem = new OrderItemDecorator(orderItem);
  return { type: UPDATE_CUSTOM_ITEM, orderItem };
}

export function removeCustomItem(orderItem) {
  return { type: REMOVE_CUSTOM_ITEM, orderItem };
}

export function updateOrderedItem(newOrderedItem) {
  return { type: UPDATE_ORDERED_ITEM, newOrderedItem };
}

export function updateOrderedField(newOrderedField) {
  return (dispatch, getState) => {
    const { order } = stateSelector(getState());
    newOrderedField = new OrderedProductOrderFieldDecorator({
      ...newOrderedField,
    });
    newOrderedField.calculateQuantityAndPricing(order);
    dispatch({ type: UPDATE_ORDERED_FIELD, newOrderedField });
  };
}

export function removeOrderParticipant(participant) {
  return { type: REMOVE_ORDER_PARTICIPANT, participant };
}

export function updateOrder(order) {
  return { type: UPDATE_ORDER, order };
}

export function validate({ submitValidate = false } = {}) {
  return { type: VALIDATE, submitValidate };
}

export function submit() {
  return { type: SUBMIT };
}

export default st.duck.createDuck(initialState, reducers, epics);



// WEBPACK FOOTER //
// ./src/modules/orders/EditOrderItemsModal/index.js